/**
 * @since 2024-05-03
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { Modal } from "antd";
import BarSaveCancel from "components/Shared/BarSaveCancel";
import InputBox from "components/Shared/InputBox";
import { changePassword } from "dataHandling/general";
import { useEffect, useState } from "react";
import { validateName } from "utils/validation";
import "./ModalPasswordChange.scss";

// Password change is its own form, separate from the profile settings: the backend route
// (strategy/changePassword/) verifies the current password before setting the new one.
const ModalPasswordChange = ({ isOpen, onCancel, onPasswordChange }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [listValidationErrors, setListValidationErrors] = useState([]);
  const [serverError, setServerError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    validateFields(newPassword, confirmPassword);
  }, [newPassword, confirmPassword]);

  const resetFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setServerError("");
  };

  const savePassword = async () => {
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    setIsSaving(true);
    setServerError("");
    const result = await changePassword(currentPassword, newPassword);
    setIsSaving(false);
    if (!result || !result.success) {
      setServerError(
        (result && result.error) || "Could not change the password."
      );
      return;
    }
    resetFields();
    onPasswordChange();
  };

  const handleCancel = () => {
    resetFields();
    onCancel();
  };

  const handleConfirmPasswordChange = (settingName, settingNewValue) => {
    setConfirmPassword(settingNewValue);
  };

  const handleCurrentPasswordChange = (settingName, settingNewValue) => {
    setCurrentPassword(settingNewValue);
    setServerError("");
  };

  const handleNewPasswordChange = (settingName, settingNewValue) => {
    setNewPassword(settingNewValue);
  };

  const showErrors = () => {
    const listErrors = serverError
      ? [...listValidationErrors, serverError]
      : listValidationErrors;
    return (
      <div className="errors-container">
        {listErrors.map((error) => (
          <div className="error" key={error}>
            {error}
          </div>
        ))}
      </div>
    );
  };

  const validateFields = (newPassword, confirmPassword) => {
    const listErrors = [];
    // Minimum length
    newPassword = String(newPassword).trim();
    confirmPassword = String(confirmPassword).trim();
    if (newPassword.length === 0) {
      // skip checks
      setListValidationErrors(listErrors);
      return;
    }
    const { isValid, errorMessage } = validateName(newPassword, 6, 30);
    if (!isValid) listErrors.push(`New password: ${errorMessage}`);
    // Different passwords
    if (newPassword !== confirmPassword) {
      listErrors.push(`The passwords do not match!`);
    }
    setListValidationErrors(listErrors);
  };

  const renderPasswordRow = (name, label, value, onUpdate) => (
    <div className="setting-item" key={`${name}.Outer`}>
      <div className="setting-row" key={`${name}.Main`}>
        <div className="setting-name" key={`${name}.Name`}>
          {label}
        </div>
        <div className="setting-value" key={`${name}-Value`}>
          <InputBox
            name={name}
            type="password"
            value={value}
            maxLength={30}
            onUpdate={onUpdate}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      open={isOpen}
      title={"CHANGE PASSWORD"}
      onCancel={handleCancel}
      footer={null}
      className="modal-password-change"
    >
      <div
        style={{
          marginTop: "40px",
          display: "flex",
          flexDirection: "column",
          alignItems: "left",
          justifyContent: "left",
          color: "rgba(255, 255, 255, 0.5)",
        }}
      >
        {renderPasswordRow(
          "CurrentPassword",
          "Current Password",
          currentPassword,
          handleCurrentPasswordChange
        )}
        {renderPasswordRow(
          "NewPassword",
          "New Password",
          newPassword,
          handleNewPasswordChange
        )}
        {renderPasswordRow(
          "ConfirmPassword",
          "Confirm Password",
          confirmPassword,
          handleConfirmPasswordChange
        )}
      </div>
      {showErrors()}
      <BarSaveCancel
        onSave={savePassword}
        onCancel={handleCancel}
        isSaveDisabled={
          isSaving ||
          currentPassword.length === 0 ||
          newPassword.length === 0 ||
          listValidationErrors.length > 0
        }
      />
    </Modal>
  );
};

export default ModalPasswordChange;
